import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "cardContainer", "cashAppContainer", "cashtagInput", "cashtagDisplay",
    "checkoutButton", "creditForm", "coinForm", "sendingOverlay",
    "sendingImage", "sendingText"
  ]

  static values = {
  exchangeId: String,
  totalUsd: String,
  applicationId: String,
  locationId: String,
  userCashtag: String,
  orderType: String,
  pigeonImageUrl: String,
  isProcessing: { type: Boolean, default: false }

}

connect() {
  this.interval = null
  console.group("🧪 Square Debug")

  // ✅ Skip Square setup for MAZA_IN
  if (this.orderTypeValue === "MAZA_IN") {
    console.warn("⚠️ Skipping Square initialization for MAZA_IN order type")
    console.groupEnd()
    this.initCashtag()
    return
  }

  console.log("🟢 Stimulus controller connected")
  console.log("📦 exchangeId:", this.exchangeIdValue)
  console.log("💵 USD Amount:", this.totalUsdValue)
  console.log("🆔 App ID:", this.applicationIdValue)
  console.log("📍 Location ID:", this.locationIdValue)
  console.log("💰 Cashtag:", this.userCashtagValue)

  this.isSandbox = this.applicationIdValue.startsWith("sandbox-")
  console.log("🧭 Detected Mode:", this.isSandbox ? "SANDBOX" : "PRODUCTION")

  console.groupEnd()

window._SquareDebug = {
  toggleMode: () => {
    this.isSandbox = !this.isSandbox;
    console.warn("⚠️ Mode manually toggled to:", this.isSandbox ? "SANDBOX" : "PRODUCTION");
  },
  getMode: () => (this.isSandbox ? "SANDBOX" : "PRODUCTION"),
  getAppId: () => this.applicationIdValue,
  getLocationId: () => this.locationIdValue
};

  this.waitForSquareSDK().then(() => {
    console.log("✅ Square SDK loaded")
    this.initSquare()
    this.initCashtag()
  }).catch(() => {
    console.error("❌ Square SDK did not load in time.")
  })
}
  waitForSquareSDK(retries = 20, delay = 200) {
    return new Promise((resolve, reject) => {
      const check = () => {
        if (window.Square) {
          resolve()
        } else if (retries > 0) {
          retries--
          setTimeout(check, delay)
        } else {
          reject()
        }
      }
      check()
    })
  }

  async initSquare() {
    try {
      const payments = window.Square.payments(this.applicationIdValue, this.locationIdValue)

      // ✅ CARD SETUP
      const card = await payments.card()
      await card.attach(this.cardContainerTarget)

     this.checkoutButtonTarget.addEventListener("click", async () => {
if (this.isProcessingValue) return // Block double click

this.isProcessingValue = true
this.showSendingAnimation("Pigeon in flight...")
this.checkoutButtonTarget.disabled = true
this.checkoutButtonTarget.innerText = "Processing… Please wait"
this.checkoutButtonTarget.classList.add("disabled", "opacity-50", "cursor-not-allowed")


// Optional: Add a CSS class if you want to style it
this.checkoutButtonTarget.classList.add("disabled", "opacity-50", "cursor-not-allowed")

  let token

  if (this.isSandbox) {
    token = "cnon:card-nonce-ok"
    console.warn("⚠️ Using hardcoded sandbox nonce:", token)
  } else {
    const result = await card.tokenize()
    if (result.status !== "OK") {
      this.hideSendingAnimation()
      this.isProcessingValue = false
      this.checkoutButtonTarget.disabled = false
      this.checkoutButtonTarget.innerText = "Pay with Card"
      console.error("❌ Card tokenization failed:", result.errors)
      alert("Card error: " + result.errors.map(e => e.message).join(", "))
      return
    }
    token = result.token
    console.log("✅ Production token generated:", token)
  }

  console.log("📤 Submitting source_id via AJAX:", token)

  try {
    const response = await fetch(`/exchanges/${this.exchangeIdValue}/credit_card_payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify({ source_id: token })
    })

    const resultJson = await response.json()

    if (resultJson.success) {
      this.startPolling()
    } else {
      this.hideSendingAnimation()
      const errorMsg = resultJson.errors ? resultJson.errors.join(", ") : resultJson.message
      console.error("❌ Payment failed:", errorMsg)
      alert("Payment failed: " + errorMsg)
      this.isProcessingValue = false
      this.checkoutButtonTarget.disabled = false
      this.checkoutButtonTarget.innerText = "Try Again"
    }
  } catch (err) {
    this.hideSendingAnimation()
    console.error("❌ AJAX error submitting payment:", err)
    alert("Error submitting payment. Please try again.")
    this.isProcessingValue = false
    this.checkoutButtonTarget.disabled = false
    this.checkoutButtonTarget.innerText = "Try Again"
  }
})


      // ✅ Skip Cash App Pay if using sandbox
      if (this.isSandbox) {
        console.warn("⚠️ Skipping Cash App Pay in sandbox")
        return
      }

      // ✅ CASH APP PAY SETUP (Production only)
      const paymentRequest = payments.paymentRequest({
        countryCode: "US",
        currencyCode: "USD",
        total: {
          amount: this.totalUsdValue,
          label: "Total"
        }
      })

      const cashAppPay = await payments.cashAppPay(paymentRequest, {
        redirectURL: "https://313.cash/exchanges/" + this.exchangeIdValue + "/processing",
        referenceId: "exchange-" + this.exchangeIdValue
      })

      await cashAppPay.attach(this.cashAppContainerTarget)

      cashAppPay.addEventListener("ontokenization", async (event) => {
        const { tokenResult, error } = event.detail
        if (error || !tokenResult?.token) {
          console.error("❌ Cash App tokenization failed", error)
          alert("Cash App Pay failed to generate a token. Please try again.")
          return
        }

        console.log("✅ Cash App Token:", tokenResult.token)
        this.isProcessingValue = true
        this.showSendingAnimation("Pigeon in flight...")

        const response = await fetch(`/exchanges/${this.exchangeIdValue}/credit_card_payment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
          },
          body: JSON.stringify({ source_id: tokenResult.token })
        })

        const result = await response.json()

        if (result.success) {
          this.startPolling()

          // ✅ Optional immediate redirect for mobile users after successful charge
          setTimeout(() => {
            window.location.href = `/exchanges/${this.exchangeIdValue}`
          }, 3000) // Leave time to read the payment confirmation before navigating.
        } else {
          this.hideSendingAnimation()
          console.error("❌ Server rejected payment:", result.message)
          alert("Payment failed: " + result.message)
          this.isProcessingValue = false
        }
      })

    } catch (error) {
      this.hideSendingAnimation()
      console.error("❌ Square Init Error:", error)
      alert("Square SDK Error: " + error.message)
    }
  }

  disconnect() {
    if (this.interval) clearInterval(this.interval)
    this.hideSendingAnimation()
  }

  initCashtag() {
    if (!this.hasCashtagInputTarget || !this.hasCashtagDisplayTarget) return

    let timer = null
    const input = this.cashtagInputTarget
    const display = this.cashtagDisplayTarget

    if (this.userCashtagValue) {
      input.value = this.userCashtagValue
      display.innerHTML = `Current Cashtag: $${this.userCashtagValue}`
      input.disabled = true
    }

    input.addEventListener("input", () => {
      clearTimeout(timer)
      const cashtag = input.value.replace(/[^a-zA-Z0-9]/g, "")
      input.value = cashtag
      display.innerHTML = `Current Cashtag: $${cashtag || 'None set'}`

      timer = setTimeout(() => {
        if (cashtag.length > 1) {
          fetch(`/exchanges/${this.exchangeIdValue}/update_cashtag`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
            },
            body: JSON.stringify({ cashtag: cashtag })
          })
          .then(resp => resp.json())
          .then(data => {
            if (!data.success) console.error("❌ Cashtag update failed", data.message)
          })
        }
      }, 1000)
    })
  }

  startPolling() {
    this.checkStatus()
    this.interval = setInterval(() => this.checkStatus(), 5000)
  }

  async checkStatus() {
    try {
      const response = await fetch(`/exchanges/${this.exchangeIdValue}/status`)
      const data = await response.json()
      if (["paid", "payout_pending", "completed", "canceled"].includes(data.status)) {
        this.hideSendingAnimation()
        clearInterval(this.interval)
        window.location.href = `/exchanges/${this.exchangeIdValue}`
      }
    } catch (err) {
      console.error("❌ Polling Error:", err)
    }
  }

  submitCoinForm() {
    if (this.isProcessingValue) return
    this.isProcessingValue = true
    this.showSendingAnimation("Pigeon in flight...")
  }

  showSendingAnimation(message = "Pigeon in flight...") {
    if (!this.hasSendingOverlayTarget || !this.hasSendingImageTarget || !this.hasSendingTextTarget) return
    this.sendingImageTarget.src = this.pigeonImageUrlValue
    this.sendingTextTarget.textContent = message
    this.sendingOverlayTarget.style.display = "flex"
  }

  hideSendingAnimation() {
    if (!this.hasSendingOverlayTarget) return
    this.sendingOverlayTarget.style.display = "none"
  }
}
